"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";

interface UseUrlModalOptions {
  /**
   * The unique ID for this modal
   */
  modalId: string;
  /**
   * The name of the URL parameter to use for the modal ID
   * @default "modal"
   */
  paramName?: string;
  /**
   * Whether to replace the current history entry instead of pushing a new one
   * @default false
   */
  replace?: boolean;
}

interface UseUrlModalReturn {
  /**
   * Whether this specific modal is open
   */
  isOpen: boolean;
  /**
   * Function to open this modal
   */
  onOpen: () => void;
  /**
   * Function to close this modal
   */
  onClose: () => void;
  /**
   * Function to toggle this modal
   */
  onToggle: () => void;
  /**
   * Function to get a URL with this modal open
   */
  getUrl: () => string;
}

/**
 * @deprecated This hook is deprecated. Use the global modal stack instead for better modal management.
 * 
 * **Migration Guide:**
 * 
 * 1. Enable URL sync at the app level:
 * ```tsx
 * // In your root layout
 * <GlobalModalStackProvider syncWithUrl urlParamName="modal">
 *   <App />
 * </GlobalModalStackProvider>
 * ```
 * 
 * 2. Replace `useUrlModal` with `useGlobalModalStack`:
 * ```tsx
 * // OLD (deprecated)
 * const { isOpen, onOpen, onClose } = useUrlModal({ modalId: "edit-profile" });
 * 
 * // NEW (recommended)
 * const { isModalOpen, openModal, closeModal } = useGlobalModalStack();
 * const isOpen = isModalOpen("edit-profile");
 * const onOpen = () => openModal("edit-profile");
 * const onClose = () => closeModal("edit-profile");
 * ```
 * 
 * 3. Or use `ResponsiveModal` with `globalModalId`:
 * ```tsx
 * // NEW (simplest)
 * <ResponsiveModal globalModalId="edit-profile" title="Edit Profile">
 *   <ModalContent />
 * </ResponsiveModal>
 * 
 * <Button onClick={() => openModal("edit-profile")}>Edit Profile</Button>
 * ```
 * 
 * **Benefits of migration:**
 * - Centralized modal state management
 * - Better z-index stacking with multiple modals
 * - Automatic URL sync (opt-in at app level)
 * - Simpler API and less boilerplate
 * - Better TypeScript support
 * 
 * @example
 * ```tsx
 * const { isOpen, onOpen, onClose } = useUrlModal({ modalId: "edit-profile" });
 * 
 * // Use in component
 * <Modal open={isOpen} onOpenChange={(open) => {
 *   if (!open) onClose();
 * }}>
 *   <ModalContent>Edit Profile</ModalContent>
 * </Modal>
 * 
 * <Button onClick={onOpen}>
 *   Edit Profile
 * </Button>
 * ```
 */
export function useUrlModal({
  modalId,
  paramName = "modal",
  replace = false,
}: UseUrlModalOptions): UseUrlModalReturn {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // Get the current modal from URL - derive state instead of managing separate state
  const urlModal = searchParams?.get(paramName);
  const isOpen = urlModal === modalId;
  
  const onOpen = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString() || "");
    params.set(paramName, modalId);
    
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    
    if (replace) {
      router.replace(newUrl);
    } else {
      router.push(newUrl);
    }
  }, [searchParams, paramName, modalId, replace, router]);
  
  const onClose = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString() || "");
    params.delete(paramName);
    
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    
    if (replace) {
      router.replace(newUrl);
    } else {
      router.push(newUrl);
    }
  }, [searchParams, paramName, replace, router]);
  
  const onToggle = useCallback(() => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isOpen, onOpen, onClose]);
  
  const getUrl = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString() || "");
    params.set(paramName, modalId);
    
    return `${window.location.pathname}?${params.toString()}`;
  }, [searchParams, paramName, modalId]);

  return {
    isOpen,
    onOpen,
    onClose,
    onToggle,
    getUrl,
  };
}
